import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../../ui/Icon';
import './money.css';

/*
 * Local visual pieces for the Ops "Money" pages (Payments, Catch-weight, Refunds, Payouts,
 * Reports), on top of the dashboard layer (styles/dash.css). Passive marks only: no edges, no
 * lift; tokens only. Not shared outside src/ops/{payments,catchweight,refunds,payouts,reports}.
 */

const clamp = (n: number, lo = 0, hi = 100) => Math.min(hi, Math.max(lo, n));
const v = (name: string, value: string | number) => ({ [name]: value }) as CSSProperties;

/** "Live · 12s": the pulse and how long since the last answer. */
export function LiveMeta({ updatedAt, now, children }: { updatedAt: number; now: number; children?: ReactNode }) {
  const s = updatedAt ? Math.max(0, Math.round((now - updatedAt) / 1000)) : null;
  const ago = s === null ? '' : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m`;
  return (
    <span>
      Live{ago ? ` · ${ago}` : ''}
      {children}
    </span>
  );
}

/**
 * Cash a rider holds against their limit: the fill goes amber near the limit and red over it;
 * a tick marks the limit itself (the scale leaves headroom so the tick is always visible).
 */
export function CashMeter({ value, limit, label }: { value: number; limit: number; label: string }) {
  const scale = Math.max(limit * 1.25, value, 1);
  const fill = clamp((Math.max(0, value) / scale) * 100);
  const tick = clamp((limit / scale) * 100);
  const tone = limit > 0 && value > limit ? 'bad' : limit > 0 && value >= limit * 0.8 ? 'warn' : 'ok';
  return (
    <span className={`mn-meter ${tone}`} role="meter" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={value} aria-label={label}>
      <i style={{ width: `${fill}%` }} />
      {limit > 0 ? <b style={{ left: `${tick}%` }} /> : null}
    </span>
  );
}

/**
 * Drift around zero with the tolerance band shaded: the bar grows from the centre to the value,
 * Ink inside the band, amber outside it.
 */
export function DriftBar({ pct, tol, scale, label }: { pct: number | null; tol: number; scale?: number; label?: string }) {
  const s = Math.max(scale ?? 0, tol * 2, Math.abs(pct ?? 0), 1);
  const band = clamp((tol / s) * 50, 0, 50);
  const x = pct === null ? 0 : clamp((Math.abs(pct) / s) * 50, 0, 50);
  const out = pct !== null && Math.abs(pct) > tol;
  return (
    <span className={`mn-drift${out ? ' out' : ''}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <span className="band" style={{ left: `${50 - band}%`, right: `${50 - band}%` }} />
      <span className="mid" />
      {pct !== null && x > 0 ? <i style={pct >= 0 ? { left: '50%', width: `${x}%` } : { right: '50%', width: `${x}%` }} /> : null}
    </span>
  );
}

/** One share row: name (and a sub line), a share bar, the figure; the bar is a meter for AT. */
export function ShareRow({ name, lead, sub, pct, value, barClass, label }: { name: ReactNode; lead?: ReactNode; sub?: ReactNode; pct: number; value: ReactNode; barClass?: string; label: string }) {
  return (
    <li className="mn-share">
      <span className="nm">
        {lead}
        <span className="t">{name}</span>
        {sub ? <span className="dv-sub">{sub}</span> : null}
      </span>
      <span className="val">{value}</span>
      <span className="bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
        <i className={barClass} style={{ width: `${clamp(pct)}%`, ...v('--w', pct) }} />
      </span>
    </li>
  );
}

/** Split of a gross amount: what the shop nets, commission, and adjustments (a thin stacked bar). */
export function SplitBar({ net, commission, adjustments }: { net: number; commission: number; adjustments: number }) {
  const total = Math.max(1, Math.max(0, net) + Math.max(0, commission) + Math.abs(adjustments));
  const w = (n: number) => `${(Math.max(0, n) / total) * 100}%`;
  return (
    <span className="mn-split" aria-hidden="true">
      <i className="net" style={{ width: w(net) }} />
      <i className="com" style={{ width: w(commission) }} />
      {adjustments ? <i className="adj" style={{ width: w(Math.abs(adjustments)) }} /> : null}
    </span>
  );
}

/** A small round status dot (ok / warn / bad / off / info). */
export function Dot({ tone, title }: { tone: 'ok' | 'warn' | 'bad' | 'off' | 'info'; title?: string }) {
  return <span className={`mn-dot ${tone}`} title={title} aria-hidden="true" />;
}

/** An info "i" with the explanation as a tooltip (and for screen readers). */
export function Hint({ text }: { text: string }) {
  return (
    <span className="mn-hint" title={text} tabIndex={0} aria-label={text} role="note">
      <span aria-hidden="true">i</span>
    </span>
  );
}

/** A calm empty line: tick icon + a few words. */
export function Quiet({ children, icon = true }: { children: ReactNode; icon?: boolean }) {
  return (
    <div className="dv-quiet">
      {icon ? <Icon name="check" /> : null}
      {children}
    </div>
  );
}

/** One pill per rider holding cash: red when over the limit, Ink when holding some, a track when none. */
export function RiderPills({ riders, cap = 16 }: { riders: { onHandCentavos: number; overLimit: boolean }[]; cap?: number }) {
  const sorted = [...riders].sort((a, b) => Number(b.overLimit) - Number(a.overLimit) || b.onHandCentavos - a.onHandCentavos);
  const shown = sorted.slice(0, cap);
  if (!shown.length) return null;
  return (
    <span className={shown.length <= 8 ? 'mn-pills few' : 'mn-pills'} aria-hidden="true">
      {shown.map((r, i) => (
        <i key={i} className={r.overLimit ? 'bad' : r.onHandCentavos > 0 ? 'on' : ''} />
      ))}
      {sorted.length > cap ? <span className="more">+{sorted.length - cap}</span> : null}
    </span>
  );
}

